"use client";

import { useEffect, useMemo, useState } from "react";
import {
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@/app/Components/Button";
import { buttonClassName } from "@/app/Components/buttonStyles";
import {
  OperationalResult,
  OperationalSource,
  OperationalState,
  OperationalStatus,
} from "@/app/Components/OperationalPage";
import { resolveEventImageUrl } from "@/app/lib/eventImages";
import {
  availableIntervals,
  DAY_END_MIN,
  DAY_START_MIN,
  formatMinutes,
  type RoomAvailabilityData,
  type RoomInfo,
} from "@/app/lib/room-helpers";
import type { CampusEvent } from "@/app/types/events";

const SLOT_SIZE = 15;

function easternNow(date = new Date()): { day: string; minutes: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    weekday: "long",
    hour: "numeric",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return {
    day: value("weekday"),
    minutes: Number(value("hour")) * 60 + Number(value("minute")),
  };
}

function searchable(value: string | null | undefined): string {
  return (value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/** Resolve only from checked-in room identity; never invent an availability key. */
export function resolveEventRoom(
  event: CampusEvent,
  rooms: RoomInfo[],
): RoomInfo | null {
  const direct = rooms.find((room) => room.roomKey === event.id);
  if (direct) return direct;

  const eventText = searchable(`${event.title} ${event.location ?? ""}`);
  const exactLabel = rooms.find((room) =>
    eventText.includes(searchable(room.label)),
  );
  if (exactLabel) return exactLabel;

  const candidates = rooms.filter((room) => {
    const building = searchable(room.building_code);
    const number = searchable(room.room_number);
    return Boolean(
      building &&
      number &&
      eventText.includes(building) &&
      eventText.includes(number),
    );
  });
  return candidates.length === 1 ? candidates[0] : null;
}

type LoadState = "idle" | "loading" | "loaded" | "error";

export default function StudySpaceRow({ event }: { event: CampusEvent }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [roomData, setRoomData] = useState<RoomAvailabilityData | null>(null);
  const [loadState, setLoadState] = useState<LoadState>("idle");
  const [loadRevision, setLoadRevision] = useState(0);
  const imageSrc = resolveEventImageUrl(event.image_url);
  const panelId = `study-space-${event.id.replace(/[^a-zA-Z0-9_-]/g, "-")}`;
  const now = useMemo(() => easternNow(), []);

  useEffect(() => {
    if (!isExpanded || roomData) return;
    const controller = new AbortController();
    setLoadState("loading");
    void fetch("/data/room_availability_spring2026.json", {
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json() as Promise<RoomAvailabilityData>;
      })
      .then((data) => {
        if (!Array.isArray(data.rooms) || typeof data.usage !== "object") {
          throw new Error("Invalid room availability payload");
        }
        setRoomData(data);
        setLoadState("loaded");
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setLoadState("error");
      });
    return () => controller.abort();
  }, [isExpanded, loadRevision, roomData]);

  const room = useMemo(
    () => (roomData ? resolveEventRoom(event, roomData.rooms) : null),
    [event, roomData],
  );
  const availability = useMemo(() => {
    if (!roomData || !room) return null;
    const busy = (roomData.usage[room.roomKey] ?? []).filter(
      (block) => block.day === now.day,
    );
    return { busy, free: availableIntervals(busy) };
  }, [now.day, room, roomData]);

  const currentStatus = useMemo(() => {
    if (loadState === "idle") {
      return { tone: "neutral" as const, text: "Schedule on demand" };
    }
    if (loadState === "loading") {
      return { tone: "info" as const, text: "Checking schedule" };
    }
    if (loadState === "error" || !availability) {
      return { tone: "warning" as const, text: "Schedule unavailable" };
    }
    return { tone: "neutral" as const, text: "Spring 2026 schedule" };
  }, [availability, loadState]);

  const gridSlots = useMemo(() => {
    if (!availability) return [];
    const currentHour = Math.floor(now.minutes / 60) * 60;
    const start = Math.max(DAY_START_MIN, currentHour);
    const slots: Array<{ time: number; isBusy: boolean }> = [];
    for (let minute = start; minute < DAY_END_MIN; minute += SLOT_SIZE) {
      slots.push({
        time: minute,
        isBusy: availability.busy.some(
          (block) => minute >= block.startMinute && minute < block.endMinute,
        ),
      });
    }
    return slots;
  }, [availability, now.minutes]);

  const freeIntervals = availability?.free
    .filter((interval) => interval.end > now.minutes)
    .map(
      (interval) =>
        `${formatMinutes(Math.max(interval.start, now.minutes))}–${formatMinutes(interval.end)}`,
    );

  const toggleExpanded = () => {
    if (isExpanded && loadState === "loading") setLoadState("idle");
    setIsExpanded((current) => !current);
  };

  return (
    <OperationalResult
      actions={
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={toggleExpanded}
            aria-expanded={isExpanded}
            aria-controls={panelId}
          >
            {isExpanded ? (
              <ChevronUpIcon aria-hidden="true" className="h-4 w-4" />
            ) : (
              <ChevronDownIcon aria-hidden="true" className="h-4 w-4" />
            )}
            {isExpanded ? "Hide schedule" : "View schedule"}
          </Button>
          <a
            href="https://libcal.rutgers.edu/spaces?lid=2558"
            target="_blank"
            rel="noopener noreferrer"
            className={buttonClassName({ variant: "primary" })}
          >
            Check room booking
            <ArrowTopRightOnSquareIcon aria-hidden="true" className="h-4 w-4" />
          </a>
        </div>
      }
    >
      <div className="min-w-0">
        <div className="flex min-w-0 flex-col gap-4 sm:flex-row">
          {imageSrc ? <div className="relative h-32 w-full shrink-0 overflow-hidden rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface-subtle)] sm:h-24 sm:w-32">
              <img
                src={imageSrc}
                alt=""
                className="h-full w-full object-cover"
              />
            <OperationalStatus className="absolute left-2 top-2">
              Study space
            </OperationalStatus>
          </div> : null}
          <div className="min-w-0 flex-1">
            <OperationalStatus tone={currentStatus.tone}>
              {currentStatus.text}
            </OperationalStatus>
            <h3 className="mt-2 break-words text-lg font-semibold text-[var(--ss-text)]">
              {event.title}
            </h3>
            {event.location ? (
              <p className="mt-1 flex items-center gap-1 text-sm text-[var(--ss-text-muted)]">
                <MapPinIcon aria-hidden="true" className="h-4 w-4" />
                {event.location}
              </p>
            ) : null}
          </div>
        </div>

        {isExpanded ? (
          <div
            id={panelId}
            className="mt-4 border-t border-[var(--ss-border)] pt-4"
          >
            {loadState === "loading" ? (
              <OperationalState
                kind="loading"
                title="Loading room schedule"
                description="Reading the checked-in Spring 2026 availability snapshot."
              />
            ) : loadState === "error" ? (
              <OperationalState
                kind="error"
                title="Room schedule unavailable"
                description="The checked-in room availability file could not be read."
                action={
                  <Button
                    onClick={() => {
                      setRoomData(null);
                      setLoadState("idle");
                      setLoadRevision((current) => current + 1);
                    }}
                  >
                    Retry
                  </Button>
                }
              />
            ) : loadState === "loaded" && !room ? (
              <OperationalState
                kind="partial"
                title="Room identity not matched"
                description="Use Rutgers booking to check this room."
              />
            ) : availability ? (
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-[var(--ss-text)]">
                    Unscheduled periods · {now.day} (Spring 2026)
                  </h4>
                  <p className="mt-1 text-sm text-[var(--ss-text-muted)]">
                    {freeIntervals?.length
                      ? freeIntervals.join(", ")
                      : "No remaining unscheduled periods in this snapshot."}
                  </p>
                </div>
                {gridSlots.length ? (
                  <div className="overflow-x-auto pb-2" aria-hidden="true">
                    <div className="flex min-w-max gap-0.5">
                      {gridSlots.map((slot, index) => (
                        <div
                          key={slot.time}
                          className="flex w-8 flex-col items-center gap-2"
                        >
                          <div
                            className={`h-10 w-8 rounded-control ${
                              slot.isBusy
                                ? "bg-[var(--ss-critical)]"
                                : "bg-[var(--ss-positive)]"
                            }`}
                          />
                          {index % 4 === 0 ? (
                            <span className="whitespace-nowrap text-xs font-medium text-[var(--ss-text-muted)]">
                              {formatMinutes(slot.time).replace(":00", "")}
                            </span>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
                <OperationalSource
                  kind="checked-in"
                  source="Spring 2026 room-availability snapshot"
                  stale
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </OperationalResult>
  );
}
